import {
  nowAppDateTimeDisplay,
  todayAppDateDisplay,
} from '@/features/investment/utils/formatAppDateTime';
import { createEmptyInvestmentState } from './emptyState';
import type {
  DecisionStatus,
  FollowUp,
  InvestmentDecision,
  InvestmentProject,
  InvestmentState,
  Lead,
  LeadStatus,
  ProjectStage,
  SubmitDecisionPayload,
  TaskItem,
  Transaction,
  UpdateProjectPayload,
  UpdateTransactionProgressPayload,
} from './types';

export type InvestmentAction =
  | { type: 'hydrate'; payload: Partial<InvestmentState> }
  | { type: 'setLeads'; leads: Lead[] }
  | { type: 'addLead'; lead: Lead }
  | { type: 'updateLead'; lead: Lead }
  | { type: 'deleteLead'; leadId: string }
  | { type: 'updateLeadStatus'; leadId: string; status: LeadStatus }
  | {
      type: 'addFollowUp';
      leadId?: string;
      projectId?: string;
      content: string;
      followUp?: FollowUp;
    }
  | { type: 'setLeadFollowUps'; leadId: string; followUps: FollowUp[] }
  | { type: 'addTask'; task: TaskItem }
  | { type: 'convertLead'; leadId: string }
  | { type: 'updateProject'; project: InvestmentProject }
  | {
      type: 'patchProject';
      projectId: string;
      payload: UpdateProjectPayload;
    }
  | { type: 'updateProjectStage'; projectId: string; stage: ProjectStage }
  | { type: 'updateDecision'; decision: InvestmentDecision }
  | {
      type: 'submitDecision';
      projectId: string;
      payload?: SubmitDecisionPayload;
    }
  | { type: 'updateTransaction'; transaction: Transaction }
  | {
      type: 'setDecisionResult';
      decisionId: string;
      result: DecisionStatus;
      conditions?: string;
    }
  | {
      type: 'updateTransactionProgress';
      transactionId: string;
      payload: UpdateTransactionProgressPayload;
    }
  | {
      type: 'completeClosing';
      transactionId: string;
      cashWaiver?: { reason: string };
    };

export const initialInvestmentState: InvestmentState =
  createEmptyInvestmentState();

export const investmentReducer = (
  state: InvestmentState,
  action: InvestmentAction,
): InvestmentState => {
  switch (action.type) {
    case 'hydrate':
      return {
        ...state,
        ...action.payload,
        leads: action.payload.leads ?? state.leads ?? [],
        projects: action.payload.projects ?? state.projects ?? [],
        decisions: action.payload.decisions ?? state.decisions ?? [],
        transactions: action.payload.transactions ?? state.transactions ?? [],
        followUps: action.payload.followUps ?? state.followUps ?? [],
      };
    case 'setLeads':
      return { ...state, leads: action.leads };
    case 'addLead':
      return { ...state, leads: [action.lead, ...state.leads] };
    case 'updateLead':
      return {
        ...state,
        leads: state.leads.map((item) =>
          item.id === action.lead.id ? action.lead : item,
        ),
      };
    case 'deleteLead':
      return {
        ...state,
        leads: state.leads.filter((item) => item.id !== action.leadId),
        followUps: state.followUps.filter(
          (item) => item.leadId !== action.leadId,
        ),
      };
    case 'updateLeadStatus':
      return {
        ...state,
        leads: state.leads.map((item) =>
          item.id === action.leadId ? { ...item, status: action.status } : item,
        ),
      };
    case 'addFollowUp':
      return {
        ...state,
        followUps: [
          action.followUp ?? {
            id: `follow-up-${state.followUps.length + 1}`,
            leadId: action.leadId,
            projectId: action.projectId,
            content: action.content,
            actor: '张三',
            time: nowAppDateTimeDisplay(),
          },
          ...state.followUps,
        ],
        leads: action.leadId
          ? state.leads.map((item) =>
              item.id === action.leadId
                ? {
                    ...item,
                    lastFollowUp: todayAppDateDisplay(),
                  }
                : item,
            )
          : state.leads,
      };
    case 'setLeadFollowUps': {
      const others = state.followUps.filter(
        (item) => item.leadId !== action.leadId,
      );
      return {
        ...state,
        followUps: [...action.followUps, ...others],
      };
    }
    case 'addTask':
      return { ...state, tasks: [action.task, ...state.tasks] };
    case 'convertLead': {
      const lead = state.leads.find((item) => item.id === action.leadId);
      if (!lead || lead.status === '已立项' || lead.status !== '初筛通过') {
        return state;
      }
      const projectId = `project-from-${lead.id}`;
      const financingNote =
        lead.financingAmount != null
          ? `线索记录本轮计划融资 ${lead.financingAmount} 万元。`
          : '线索未记录计划融资金额。';
      return {
        ...state,
        leads: state.leads.map((item) =>
          item.id === lead.id
            ? {
                ...item,
                status: '已立项',
                relatedProjectId: projectId,
                nextAction: '已转入投资项目管理',
              }
            : item,
        ),
        projects: [
          {
            id: projectId,
            sourceLeadId: lead.id,
            name: lead.name,
            company: lead.company,
            industry: lead.industry,
            round: lead.round,
            stage: '项目评估',
            targetFund: '待选择',
            financingAmount: lead.financingAmount,
            investmentAmount: 0,
            preMoneyValuation: lead.preMoneyValuation ?? 0,
            holdingRatio: 0,
            manager: lead.owner,
            thesis: `${financingNote}待补充拟投资金额与核心投资逻辑。`,
            summary: lead.summary ?? '',
            product: '',
            market: '',
            tech: '',
            team: '',
            financeNote: '',
            highlight: lead.highlight ?? '',
            risk: '',
            judgment: '',
            riskLevel: '中',
            nextAction: '补充拟投资金额与投前估值',
            updatedAt: new Date().toISOString().slice(0, 10),
          },
          ...state.projects,
        ],
      };
    }
    case 'updateProject':
      return {
        ...state,
        projects: state.projects.some((item) => item.id === action.project.id)
          ? state.projects.map((item) =>
              item.id === action.project.id ? action.project : item,
            )
          : [action.project, ...state.projects],
      };
    case 'patchProject': {
      const existing = state.projects.find(
        (item) => item.id === action.projectId,
      );
      if (!existing) return state;
      return {
        ...state,
        projects: state.projects.map((item) =>
          item.id === action.projectId
            ? ({ ...item, ...action.payload } as InvestmentProject)
            : item,
        ),
      };
    }
    case 'updateProjectStage':
      return {
        ...state,
        projects: state.projects.map((item) =>
          item.id === action.projectId
            ? { ...item, stage: action.stage }
            : item,
        ),
      };
    case 'updateDecision':
      return {
        ...state,
        decisions: state.decisions.some(
          (item) => item.id === action.decision.id,
        )
          ? state.decisions.map((item) =>
              item.id === action.decision.id ? action.decision : item,
            )
          : [action.decision, ...state.decisions],
      };
    case 'submitDecision':
    case 'setDecisionResult':
      // 投决由接口返回真实记录后走 updateDecision，失败时不要写入本地假数据。
      return state;
    case 'updateTransaction':
      return {
        ...state,
        transactions: state.transactions.some(
          (item) => item.id === action.transaction.id,
        )
          ? state.transactions.map((item) =>
              item.id === action.transaction.id ? action.transaction : item,
            )
          : [action.transaction, ...state.transactions],
      };
    case 'updateTransactionProgress':
      return {
        ...state,
        transactions: state.transactions.map((item) =>
          item.id === action.transactionId
            ? { ...item, ...action.payload }
            : item,
        ),
      };
    case 'completeClosing': {
      const transaction = state.transactions.find(
        (item) => item.id === action.transactionId,
      );
      if (!transaction || transaction.closingStatus === '已完成') return state;
      const finalInvestmentAmount = transaction.investmentAmount;
      return {
        ...state,
        transactions: state.transactions.map((item) =>
          item.id === transaction.id
            ? {
                ...item,
                stage: '完成交割',
                closingStatus: '已完成',
                paymentStatus: '已付款',
                investmentAmount: finalInvestmentAmount,
              }
            : item,
        ),
        projects: state.projects.map((item) =>
          item.id === transaction.projectId
            ? {
                ...item,
                stage: '已交割',
                investmentAmount: finalInvestmentAmount,
              }
            : item,
        ),
        investedAmount: state.investedAmount + finalInvestmentAmount,
        activities: [
          {
            id: `activity-${state.activities.length + 1}`,
            title: '苏州精控机器人完成交割',
            description: `最终投资金额 ${finalInvestmentAmount} 万元，已转入投后管理`,
            actor: '张三',
            time: '2026-08-10 17:30',
            type: '完成交割',
          },
          ...state.activities,
        ],
      };
    }
    default:
      return state;
  }
};
